import type { QueryKey } from '@tanstack/react-query';

import { queryClient } from '@/query/queryClient';
import {
  ASSIGNMENT_ROOT,
  SESSION_ROOT,
  activeAssignmentId,
  roleSessionKeys,
  sessionKeys,
} from '@/query/keys';

/**
 * The session, read and written outside React (UI state plan, phase 1).
 *
 * The session IS server data (who is signed in, which role is active, what
 * the role may do), so it lives in the TanStack Query cache, in page memory
 * only. Nothing here touches localStorage: a hard refresh starts empty and
 * loads the session from the identity service again (auth/sessionBoot.ts).
 *
 * React reads it through `useSession()` (auth/SessionProvider.tsx); code that
 * cannot use a hook (the static `ApiProvider`, auth/*.ts) uses these accessors.
 *
 * No React and no ApiProvider import here (ApiProvider imports this module).
 */

/** The LoginData `user` (profile, currency, terms flags, landing modules). */
export type SessionUser = Record<string, any>;

/** GET User/PermissionsMap: the permissions the active role holds (`name: true`). */
export type PermissionsMap = Record<string, boolean>;

/** identity me/details: the landing modules of the active role. */
export interface SessionModules {
  home_module?: any[];
  brand_module?: any[];
  property_module?: any[];
  [key: string]: unknown;
}

/** Session data is never garbage-collected while the page lives. */
export const SESSION_GC_TIME = Infinity;

export const readSessionData = <T>(key: QueryKey): T | undefined =>
  queryClient.getQueryData<T>(key);

export const writeSessionData = <T>(key: QueryKey, data: T): void => {
  queryClient.setQueryDefaults(key, { gcTime: SESSION_GC_TIME });
  queryClient.setQueryData(key, data);
};

export const removeSessionData = (key: QueryKey): void => {
  queryClient.removeQueries({ queryKey: key, exact: true });
};

// ---- the signed-in user ------------------------------------------------------------

/** The user as LoginData answered it (no context-type shim), or null when signed out. */
export const getSessionUser = (): SessionUser | null => {
  const user = readSessionData<SessionUser | null>(sessionKeys.user);
  return user && typeof user === 'object' ? user : null;
};

export const setSessionUser = (user: SessionUser | null): void => {
  if (user && typeof user === 'object') writeSessionData(sessionKeys.user, { ...user });
  else removeSessionData(sessionKeys.user);
};

/** Merge `patch` into the signed-in user (profile saved, terms accepted, ...). */
export const patchSessionUser = (patch: Record<string, unknown>): SessionUser | null => {
  const user = getSessionUser();
  if (!user) return null;
  const next = { ...user, ...patch };
  writeSessionData(sessionKeys.user, next);
  return next;
};

/** The signed-in user's id, or null. */
export const getSessionUserId = (): number | null => {
  const id = Number(getSessionUser()?.id);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/** True once this page holds a signed-in user (after sign-in or the hard-refresh load). */
export const isSignedIn = (): boolean => getSessionUser() !== null;

// ---- the active role's permissions ---------------------------------------------------

export const getPermissionsMap = (
  assignmentId: number | null = activeAssignmentId(),
): PermissionsMap | null => {
  const map = readSessionData<PermissionsMap | null>(roleSessionKeys.permissions(assignmentId));
  return map && typeof map === 'object' ? map : null;
};

export const setPermissionsMap = (
  map: PermissionsMap,
  assignmentId: number | null = activeAssignmentId(),
): void => {
  // identity answers `[]` for "no permission held" (part 7l)
  const value = map && typeof map === 'object' && !Array.isArray(map) ? map : {};
  writeSessionData(roleSessionKeys.permissions(assignmentId), value);
};

/** The active role's permission map is loaded (PageGate waits for this). */
export const permissionsLoaded = (): boolean => getPermissionsMap() !== null;

/** True only when the active role holds `key` (a permission not held is absent from the map). */
export const sessionHasPermission = (key: string): boolean => getPermissionsMap()?.[key] === true;

// ---- the active role's landing modules and master data ----------------------------

export const getSessionModules = (
  assignmentId: number | null = activeAssignmentId(),
): SessionModules | null => {
  const modules = readSessionData<SessionModules | null>(roleSessionKeys.modules(assignmentId));
  return modules && typeof modules === 'object' ? modules : null;
};

export const setSessionModules = (
  modules: SessionModules,
  assignmentId: number | null = activeAssignmentId(),
): void => {
  writeSessionData(roleSessionKeys.modules(assignmentId), modules);
};

export const getSessionCommonData = (
  assignmentId: number | null = activeAssignmentId(),
): Record<string, any> | null => {
  const data = readSessionData<Record<string, any> | null>(
    roleSessionKeys.commonData(assignmentId),
  );
  return data ?? null;
};

export const setSessionCommonData = (
  data: Record<string, any>,
  assignmentId: number | null = activeAssignmentId(),
): void => {
  writeSessionData(roleSessionKeys.commonData(assignmentId), data);
};

/** The active role's session data is in memory (nothing to load on this page). */
export const roleSessionLoaded = (): boolean =>
  getPermissionsMap() !== null && getSessionModules() !== null && getSessionCommonData() !== null;

// ---- the page load's session check ---------------------------------------------------

/**
 * How the page load's "who is signed in" check ended (auth/sessionBoot.ts):
 * - `signed-in`: the session is in memory;
 * - `signed-out`: nobody is signed in in this tab;
 * - `unavailable`: a token exists but the identity service did not answer, or
 *   the active role's permission map did not arrive (retry screen).
 * null while it runs.
 */
export type BootOutcome = 'signed-in' | 'signed-out' | 'unavailable';

let bootOutcome: BootOutcome | null = null;
const bootListeners = new Set<() => void>();

export const getBootOutcome = (): BootOutcome | null => bootOutcome;

export const setBootOutcome = (outcome: BootOutcome | null): void => {
  if (bootOutcome === outcome) return;
  bootOutcome = outcome;
  bootListeners.forEach((listener) => listener());
};

export const subscribeBootOutcome = (listener: () => void): (() => void) => {
  bootListeners.add(listener);
  return () => {
    bootListeners.delete(listener);
  };
};

// ---- following the session (React: auth/SessionProvider.tsx) --------------------------

/** A cache entry of the session: `['session', ...]` or `['a', <id>, 'session', ...]`. */
export const isSessionKey = (key: QueryKey): boolean =>
  Array.isArray(key) &&
  (key[0] === SESSION_ROOT || (key[0] === ASSIGNMENT_ROOT && key[2] === SESSION_ROOT));

const sessionListeners = new Set<() => void>();
let stopWatchingCache: (() => void) | null = null;

/**
 * Calls `listener` whenever a session entry of the query cache changes (one
 * cache subscription for the whole app, whatever the number of listeners).
 */
export const subscribeSession = (listener: () => void): (() => void) => {
  sessionListeners.add(listener);
  stopWatchingCache ??= queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'added' && event.type !== 'removed' && event.type !== 'updated') return;
    if (!isSessionKey(event.query.queryKey)) return;
    sessionListeners.forEach((l) => l());
  });
  return () => {
    sessionListeners.delete(listener);
    if (sessionListeners.size === 0 && stopWatchingCache) {
      stopWatchingCache();
      stopWatchingCache = null;
    }
  };
};
